// src/pages/customer-panel/live-conversations/AgentSpacePage.js
// Agent Performance Analytics Dashboard (Admin Only)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Alert,
    Rating,
    Divider,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const headCellSx = { fontFamily, fontWeight: 700, fontSize: 13, color: '#475569' };
const cellSx = { fontFamily, fontSize: 13 };

// Stat card component
const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <Card
        elevation={0}
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Icon sx={{ color, fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily,
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#94a3b8',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h4"
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            color: '#1e293b',
                            mt: 0.5,
                            lineHeight: 1.2,
                        }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ fontFamily, fontSize: 12, color: '#64748b', mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default function AgentSpacePage() {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || '';
    const isAdmin = userRole === 'admin';

    // State
    const [analytics, setAnalytics] = useState(null);
    const [agentBreakdown, setAgentBreakdown] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Set default date range (last 30 days)
    useEffect(() => {
        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        setEndDate(now.toISOString().split('T')[0]);
        setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    }, []);

    // Fetch analytics
    const fetchAnalytics = useCallback(async () => {
        if (!isAdmin) return;
        try {
            setLoading(true);
            const params = {};
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;

            const response = await axiosInstance.get('/live-chat/analytics', { params });
            const data = response.data?.data || response.data || {};
            const summary = data.summary || data;

            setAnalytics({
                total_sessions: summary.total_sessions || data.total_sessions || 0,
                avg_wait_time: (summary.avg_wait_time_minutes || 0) * 60 || data.avg_wait_time || 0,
                avg_handle_time: (summary.avg_handling_time_minutes || 0) * 60 || data.avg_handle_time || 0,
                avg_rating: summary.avg_customer_rating || data.avg_rating || 0,
            });

            const agents = data.agents || data.agent_breakdown || [];
            setAgentBreakdown(Array.isArray(agents) ? agents.map(a => ({
                ...a,
                agent_name: a.agent_name || a.name,
                sessions: a.total_chats || a.sessions || 0,
                avg_response_time: (a.avg_handling_time_minutes || 0) * 60 || a.avg_response_time || 0,
                avg_rating: a.avg_rating || 0,
                resolution_rate: a.resolution_rate || null,
            })) : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching analytics:', err);
            setError(err.response?.data?.message || 'Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    }, [isAdmin, startDate, endDate]);

    useEffect(() => {
        if (startDate && endDate) {
            fetchAnalytics();
        }
    }, [fetchAnalytics, startDate, endDate]);

    // Format seconds to readable time
    const formatSeconds = (seconds) => {
        if (!seconds && seconds !== 0) return '--';
        const secs = Math.round(seconds);
        if (secs < 60) return `${secs}s`;
        const mins = Math.floor(secs / 60);
        const remainSecs = secs % 60;
        if (mins < 60) return `${mins}m ${remainSecs}s`;
        const hrs = Math.floor(mins / 60);
        const remainMins = mins % 60;
        return `${hrs}h ${remainMins}m`;
    };

    // Access denied for non-admins
    if (!isAdmin) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert
                    severity="warning"
                    icon={<LockIcon />}
                    sx={{
                        fontFamily,
                        borderRadius: '12px',
                        '& .MuiAlert-message': { fontFamily },
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 600, mb: 0.5 }}>
                        Access Denied
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: 14 }}>
                        Agent Space is only available to administrators. Your current role is{' '}
                        <strong>{userRole || 'unknown'}</strong>. Please contact your team admin for access.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BarChartIcon sx={{ color: ACCENT, fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        Agent Space
                    </Typography>
                </Box>

                {/* Date Range Filter */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        type="date"
                        size="small"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: 13 } }}
                        InputProps={{ sx: { fontFamily, fontSize: 13, borderRadius: '10px' } }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: 13 } }}
                        InputProps={{ sx: { fontFamily, fontSize: 13, borderRadius: '10px' } }}
                    />
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, fontFamily }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={40} sx={{ color: ACCENT }} />
                </Box>
            ) : (
                <>
                    {/* Stat Cards */}
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                icon={GroupIcon}
                                title="Total Sessions"
                                value={analytics?.total_sessions?.toLocaleString() || '0'}
                                subtitle="In selected period"
                                color="#3b82f6"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                icon={AccessTimeIcon}
                                title="Avg Wait Time"
                                value={formatSeconds(analytics?.avg_wait_time)}
                                subtitle="Customer queue time"
                                color="#f59e0b"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                icon={TimerIcon}
                                title="Avg Handle Time"
                                value={formatSeconds(analytics?.avg_handle_time)}
                                subtitle="Per session"
                                color="#8b5cf6"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard
                                icon={StarIcon}
                                title="Avg Rating"
                                value={
                                    analytics?.avg_rating
                                        ? Number(analytics.avg_rating).toFixed(1)
                                        : '--'
                                }
                                subtitle="Customer satisfaction"
                                color={ACCENT}
                            />
                        </Grid>
                    </Grid>

                    {/* Agent Breakdown Table */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ px: 2.5, py: 2, bgcolor: '#f8fafc' }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
                                Agent Performance Breakdown
                            </Typography>
                        </Box>
                        <Divider />
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#fafbfc' }}>
                                        <TableCell sx={headCellSx}>Agent Name</TableCell>
                                        <TableCell sx={headCellSx} align="center">Sessions</TableCell>
                                        <TableCell sx={headCellSx} align="center">Avg Response Time</TableCell>
                                        <TableCell sx={headCellSx} align="center">Avg Rating</TableCell>
                                        <TableCell sx={headCellSx} align="center">Resolution Rate</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {agentBreakdown.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: 14 }}>
                                                    No agent data available for the selected period
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        agentBreakdown.map((agent, idx) => (
                                            <TableRow
                                                key={agent.agent_id || idx}
                                                hover
                                                sx={{ '&:hover': { bgcolor: `${ACCENT}06` } }}
                                            >
                                                <TableCell sx={cellSx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Box
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: '8px',
                                                                bgcolor: `${ACCENT}15`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontFamily,
                                                                fontWeight: 700,
                                                                fontSize: 13,
                                                                color: ACCENT,
                                                            }}
                                                        >
                                                            {(agent.agent_name || agent.name || '?')[0]?.toUpperCase()}
                                                        </Box>
                                                        <Typography sx={{ fontFamily, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                                                            {agent.agent_name || agent.name || 'Unknown'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                                                        {agent.sessions || agent.total_sessions || 0}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    {formatSeconds(
                                                        agent.avg_response_time ||
                                                        agent.avgResponseTime ||
                                                        0
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Rating
                                                            value={agent.avg_rating || agent.avgRating || 0}
                                                            readOnly
                                                            size="small"
                                                            precision={0.5}
                                                        />
                                                        <Typography sx={{ fontFamily, fontSize: 12, color: '#64748b' }}>
                                                            {agent.avg_rating || agent.avgRating
                                                                ? Number(agent.avg_rating || agent.avgRating).toFixed(1)
                                                                : '--'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={cellSx}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 600,
                                                            fontSize: 14,
                                                            color:
                                                                (agent.resolution_rate || agent.resolutionRate || 0) >= 80
                                                                    ? ACCENT
                                                                    : (agent.resolution_rate || agent.resolutionRate || 0) >= 50
                                                                    ? '#f59e0b'
                                                                    : '#ef4444',
                                                        }}
                                                    >
                                                        {agent.resolution_rate != null
                                                            ? `${Number(agent.resolution_rate).toFixed(1)}%`
                                                            : agent.resolutionRate != null
                                                            ? `${Number(agent.resolutionRate).toFixed(1)}%`
                                                            : '--'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
}
